import React from 'react';
import { observer } from 'mobx-react';
import { Button, Chip, Typography } from '@mui/material';

import { useUploadHistoryStore } from '../../hooks/useUploadHistoryStore';
import Row from '../ui/core/Row';
import AllFiltersButton from './components/filters-components/AllFiltersButton';
import UploadHistoryExportCsvButton from './components/filters-components/UploadHistoryExportCsvButton';
import UploadHistoryColumnVisibilityButton from './components/filters-components/UploadHistoryColumnVisibilityButton';
import RefreshIcon from '../icons/RefreshIcon';

const DATE_FILTERS = [
  {
    label: 'Today',
    value: 'today',
  },
  {
    label: 'This Week',
    value: 'this_week',
  },
  {
    label: 'Last Week',
    value: 'last_week',
  },
  {
    label: 'This Month',
    value: 'this_month',
  },
  {
    label: 'Last Month',
    value: 'last_month',
  },
  {
    label: 'This Year',
    value: 'this_year',
  }
];

const UploadHistoryFilters: React.FC = observer(() => {
  const store = useUploadHistoryStore();

  const handleDateFilterClick = (filterValue: string) => {
    // Toggle: if already selected, deselect and reset to default (last 30 days)
    if (store.selectedDateFilter === filterValue) {
      store.clearDateRangeFilter();
      store.loadDailyStats();
      store.loadFileList(undefined, 1); // Reset to page 1 when filter changes
    } else {
      store.applyQuickDateFilter(filterValue);
      store.loadDailyStats();
      store.loadFileList(undefined, 1); // Reset to page 1 when filter changes
    }
  };

  const handleRefresh = () => {
    store.loadDailyStats();
    store.loadFileList(undefined, undefined, false, false, true);
  };

  return (
    <Row sx={{ 
      justifyContent: 'space-between', 
      padding: { xs: '10px', sm: '10px', md: '10px', lg: '10px', xl: '20px' }, 
      height: 'auto', 
      gap: { xs: 1, sm: 1.5, md: 1.5, lg: 2 }, 
      flexWrap: 'wrap', 
      alignItems: 'center' 
    }}>
      <Row sx={{ gap: { xs: 1, sm: 1.5, md: 1.5, lg: 2 }, alignItems: 'center', flexWrap: 'wrap', height: 'auto', width: 'auto' }}>
        <Typography sx={{ whiteSpace: 'nowrap' }}>
          Quick filters:
        </Typography>

        {DATE_FILTERS.map((filter) => {
          const isSelected = store.selectedDateFilter === filter.value;
          return (
            <Chip
              key={filter.value}
              label={filter.label}
              variant="outlined"
              size="small"
              onClick={() => handleDateFilterClick(filter.value)}
              sx={(theme) => ({ 
                cursor: 'pointer',
                backgroundColor: isSelected 
                  ? theme.palette.primary.main 
                  : theme.palette.secondary.main,
                borderColor: isSelected 
                  ? theme.palette.primary.main 
                  : theme.palette.divider,
                color: isSelected 
                  ? theme.palette.primary.contrastText 
                  : theme.palette.text.primary,
                fontWeight: 400,
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: isSelected 
                    ? `${theme.palette.primary.dark} !important`
                    : 'white !important',
                  color: isSelected 
                    ? `${theme.palette.primary.contrastText} !important`
                    : `${theme.palette.text.primary} !important`,
                },
                '&:active': {
                  backgroundColor: isSelected 
                    ? theme.palette.primary.main 
                    : theme.palette.secondary.main,
                  boxShadow: 'none',
                }
              })}
            />
          );
        })}
      </Row>

      <Row sx={{ gap: 1, justifyContent: 'flex-end', alignItems: 'center', height: 'auto', width: 'auto' }}>
        <UploadHistoryExportCsvButton />

        <AllFiltersButton />

        <UploadHistoryColumnVisibilityButton />

        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontWeight: 400,
            minWidth: '80px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            }
          })}
        >
          Refresh
        </Button>
      </Row>

    </Row>
  );
});

export default UploadHistoryFilters;

